<template>
  <div data-component-group="filter-forms" data-component="reports-cpm">
    <FiltersForm
      :has-filters="hasFilters"
      :is-skeleton-loading="isSkeletonLoading"
      :is-global-country-filter="false"
      @filter="onFilter"
      @clear="onClearFilters"
    >
      <!--TODO drop on refactoring-->
      <template v-if="false">
        <v-autocomplete
          clearable
          v-model="business"
          :items="businesses"
          :loading="isLoadingBusinesses"
          prepend-inner-icon="home"
          :return-object="true"
          item-title="name"
          item-value="id"
          :label="$t('business')"
          v-model:search="businessSearch"
        />

        <v-autocomplete
          clearable
          v-model="merchant"
          :items="merchants"
          :loading="isLoadingMerchants"
          prepend-inner-icon="person"
          :return-object="true"
          item-title="name"
          :label="$t('merchant')"
          :placeholder="$t('not_defined')"
          class="cpm-filter-merchant"
          v-model:search="merchantSearch"
        />
      </template>

      <base-date-picker
        ref="datePicker"
        :date="dates"
        :default-date="defaultDateRange"
        placeholder="Select date range"
        :config="dateConfig"
        :on-change="onChangeDate"
        :hasClear="false"
      />
    </FiltersForm>
  </div>
</template>

<script>
  import { v1 as uuidv1 } from 'uuid';
  import { isEqual } from 'lodash-es';
  import FiltersForm from './FiltersBase';
  import filterUrlSync from '@/mixins/filterUrlSync';
  import {isNum, isStr, debounce} from '@/utils';
  import { inject } from 'vue';

  const debouncer = debounce({
    id: uuidv1(),
    timeout: 500
  });

  // TODO: Translate component

  export default {
    mixins: [filterUrlSync],
    components: {
      FiltersForm,
    },
    props: {
      isSkeletonLoading: {
        type: Boolean,
        default: false,
      },
      title: {
        type: String,
        default() {
          // return this.$t('reports');
          return inject('i18n').t('reports');
        },
      },
    },
    emits: ['clearFilters', 'filter'],
    data() {
      return {
        urlConfig: [
          { field: 'dates', urlKey: 'dates', type: 'string' },
          {
            // Both looked up by id: the value the URL names can sit outside the
            // page the search fetch returns, and it has to end up in the options.
            field: 'business', urlKey: 'business', type: 'number', prop: 'id',
            fetchByIds: 'getBusinessesByIds',
          },
          {
            field: 'merchant', urlKey: 'merchant', type: 'number', prop: 'id',
            fetchByIds: 'getMerchantsByIds',
          },
        ],
        dates: this.setDefaultDateRange(),
        // Always the true current-month baseline, not this.dates: whenever a
        // URL date range restores, setDefaultDateRange() (called above with
        // no ignoreUrl) returns that same restored value, and copying it here
        // too would make hasFilters's date comparison always report false.
        defaultDateRange: this.setDefaultDateRange(true),
        dateConfig: {
          mode: 'range',
        },
        business: null,
        merchant: null,
        businessSearch: '',
        merchantSearch: '',
        isLoadingBusinesses: false,
        isLoadingMerchants: false,
        isRefreshingBusinesses: false,
        isRefreshingMerchants: false,
      };
    },
    computed: {
      businesses() {
        const items = this.$store.getters['businesses/items'];
        // A business restored from the URL is looked up by id, so it is not in
        // the listing the store holds. Keep it among the options.
        return this.business && !items.some(({ id }) => id === this.business.id)
          ? [...items, this.business]
          : items;
      },
      merchants() {
        return this.$store.getters['pepper/merchants'];
      },
      hasFilters() {
        const { business = {}, dates, defaultDateRange, canSeeExtendedTable } = this;
        return canSeeExtendedTable
          ? dates.length > 0 && isNum(business?.id) && !isEqual( dates, defaultDateRange)
          : dates.length > 0 && !isEqual( dates, defaultDateRange);
      },
      canSeeExtendedTable() {
        return this.$ability.can('view', 'extended-cpm-reports');
      },
    },
    watch: {
      business() {
        this.isRefreshingBusinesses = true;
      },
      merchant() {
        this.isRefreshingMerchants = true;
      },
      businessSearch(nextSearch) {
        if (isStr(nextSearch)) {
          if (this.isRefreshingBusinesses) {
            this.isRefreshingBusinesses = false;
            return false;
          }
          debouncer(() => this.getBusinesses(nextSearch));
        }
      },
      merchantSearch(nextSearch) {
        if (isStr(nextSearch)) {
          if (this.isRefreshingMerchants) {
            this.isRefreshingMerchants = false;
            return false;
          }
          debouncer(() => this.getMerchants(nextSearch));
        }
      },
    },
    created() {
      const { canSeeExtendedTable, setDefaultDateRange, onFilter } = this;

      this.dates = setDefaultDateRange();
      // Always the true current-month baseline, not this.dates — see the
      // same note in data().
      this.defaultDateRange = setDefaultDateRange(true);

      if (!canSeeExtendedTable) {
        onFilter();
      }
    },
    methods: {
      getBusinessesByIds(ids) {
        this.isLoadingBusinesses = true;
        return this.$store
          .dispatch('businesses/getItemsByIds', { ids })
          .finally(() => {
            this.isLoadingBusinesses = false;
          });
      },
      getMerchantsByIds(ids) {
        this.isLoadingMerchants = true;
        return this.$store
          .dispatch('pepper/getMerchantsByIds', { ids })
          .finally(() => {
            this.isLoadingMerchants = false;
          });
      },
      getBusinesses(search = '') {
        const query = {
          per_page: -1,
          search,
        };

        this.isLoadingBusinesses = true;

        return this.$store
          .dispatch('businesses/getItems', query)
          .then((res) => {
            this.isLoadingBusinesses = false;
            return res;
          })
          .catch((err) => {
            this.isLoadingBusinesses = false;
            return err;
          });
      },
      getMerchants(search = '') {
        const query = {
          search,
        };

        return this.$store.dispatch('pepper/getMerchants', query);
      },
      setDefaultDateRange(ignoreUrl = false) {
        // clearFiltersUrl updates the route via router.replace, which is async —
        // this.$route.query still carries the dates being cleared when onClearFilters
        // calls this synchronously right after. ignoreUrl lets it skip straight to
        // the current month instead of reading its own about-to-be-cleared value.
        const urlDateRange = ignoreUrl ? false : this.getDateRange();
        const rangeStart = urlDateRange
          ? urlDateRange.from
          : this.$dayjs().startOf('month').format('YYYY-MM-DD');
        const rangeEnd = urlDateRange
          ? urlDateRange.to
          : this.$dayjs().endOf('month').format('YYYY-MM-DD');

        return `${rangeStart} - ${rangeEnd}`;
      },
      getDateRange() {
        // urlConfig's urlKey for this field is 'dates' (matching the actual
        // ?dates=... this page writes), not 'date_range' — that key is
        // Reports.vue's, and reading it here meant a deep link's date range
        // was always silently ignored, falling back to the default month.
        const { dates: date_range } = this.$route.query;
        let dateRange = false;

        if (date_range) {
          const isValidDate = (item) =>
            this.$dayjs(item, 'YYYY-MM-DD', true).isValid();
          const dateItems = date_range.split(' - ');

          if (isValidDate(dateItems[0]) && isValidDate(dateItems[1])) {
            dateRange = {
              from: dateItems[0],
              to: dateItems[1],
            };
          }
        }

        return dateRange;
      },
      onChangeDate(dates) {
        this.dates = dates;
      },
      onFilter() {
        let { business = null, merchant = null, dates } = this;

        this.syncFiltersToUrl();

        // `dates` is a "from - to" string in normal use, but the URL restore
        // fires onFilter before setDefaultDateRange has necessarily run.
        dates = typeof dates === 'string' ? dates.split(' - ') : dates;
        const filters = isNum(business?.id)
          ? { business, merchant, dates }
          : { dates };

        this.$emit('filter', filters);
      },
      onClearFilters() {
        this.business = null;
        this.merchant = null;
        this.dates = this.setDefaultDateRange(true);
        this.defaultDateRange = this.dates;
        // `defaultDate` only pushes into the calendar when it actually changes,
        // and it can already equal this same default from mount — so the
        // dropdown would keep showing whatever the user last picked there.
        this.$refs.datePicker.setDate(this.defaultDateRange);

        this.clearFiltersUrl();

        const { business, merchant, dates } = this;
        const filters = isNum(business?.id)
          ? { business, merchant, dates }
          : { dates };

        this.$emit('clearFilters', filters);
      },
    },
  };
</script>

<style lang="scss">

</style>
